import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client';

import DeleteModal from '@atom/components/common/DeleteModal';
import InheritanceLockIcon from '@atom/components/workTemplate/InheritanceLockIcon';
import WorkTemplateContext from '@atom/components/workTemplate/WorkTemplateContext';
import { WORK_TEMPLATE_TASK_MATERIAL_DELETE } from '@atom/graph/workTemplate';
import { Icon, IconButton, Snackbar, Tooltip } from '@atom/mui';
import { TaskMaterialItem } from '@atom/types/task';
import {
  InheritedComponentType,
  WorkOrderTemplateMaterialDeleteInput,
} from '@atom/types/workTemplate';
import schemaUtilities from '@atom/utilities/schemaUtilities';
import { isComponentInherited } from '@atom/utilities/workTemplateUtilities';

import classes from './materialSection.module.css';

interface Props {
  material: TaskMaterialItem;
}

const MaterialTile = ({ material }: Props) => {
  const { workTemplate, task, refetch } = useContext(WorkTemplateContext);

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const [removeMaterial] = useMutation<
    void,
    { input: WorkOrderTemplateMaterialDeleteInput }
  >(WORK_TEMPLATE_TASK_MATERIAL_DELETE, {
    onCompleted: res => refetch(),
  });

  const iconStyle = {
    color: schemaUtilities.getSchemaColorFromColorId(material.colorId),
  };

  const isDisabled = workTemplate?.published;

  const handleRemoveMaterial = async () => {
    setDeleteModalOpen(false);

    Snackbar.info({
      message: 'Deleting...',
    });

    await removeMaterial({
      variables: {
        input: {
          workTemplateId: workTemplate.id,
          taskId: task.id,
          assetId: material.assetId,
        },
      },
    });

    Snackbar.info({
      message: `Removed Material.`,
    });

    refetch();
  };

  const isItemInherited = isComponentInherited(
    InheritedComponentType.TASK_MATERIAL,
    task?.inheritedComponents || [],
    material?.assetId,
  );

  const actionButton = isItemInherited ? (
    <div className={classes.lockContainer}>
      <InheritanceLockIcon
        tooltip="Inherited Equipment and Material cannot be removed or edited."
        placement="bottom-start"
      />
    </div>
  ) : isDisabled ? (
    <div />
  ) : (
    <IconButton
      onClick={() => setDeleteModalOpen(true)}
      tooltip="Remove"
      edge="end"
    >
      <Icon>close</Icon>
    </IconButton>
  );

  return (
    <>
      <div className={classes.tileContainer}>
        <div className={classes.tileContentContainer}>
          <div className={classes.tileIconContainer}>
            <Icon style={iconStyle}>category</Icon>
          </div>
          <div className={classes.tileContent}>
            <Tooltip title={material.name}>
              <div className={classes.tileTitle}>{material.name}</div>
            </Tooltip>
          </div>
        </div>
        {actionButton}
      </div>
      <DeleteModal
        open={deleteModalOpen}
        onConfirm={handleRemoveMaterial}
        onCancel={() => setDeleteModalOpen(false)}
        title="Remove Equipment or Material From Task"
        content="Are you sure you want to remove this item from the task?"
        cancelText="Cancel"
        confirmText="Remove"
      />
    </>
  );
};

export default MaterialTile;
